import React from "react";
import {
  Box,
  Divider,
  List,
  Typography,
  Chip,
  colors,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  Project,
  ProjectStatus,
  ReportProgress,
  ReportProgressStatus,
} from "../../types/project";
import { Place } from "@mui/icons-material";
import { formatWithTimezone } from "../../utils/dateHelper";
import { EmptyState } from "../../components/global";

const ProjectNewsPanel = ({
  data,
  isLoading,
  emptyText,
}: {
  data: Project[];
  isLoading: boolean;
  emptyText?: string;
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [waitingApprovalJobs, setWaitingApprovalJobs] = React.useState<
    Project[]
  >([]);
  const inProgressJobs = data?.filter(
    (project: Project) => project.status === ProjectStatus.IN_PROGRESS
  );

  React.useMemo(() => {
    const resultList: Project[] = [];
    inProgressJobs?.filter((inProgressJob) => {
      return inProgressJob.resources
        ?.filter((resource) => !!resource.hired_at)
        .map((hired) => {
          hired.report_progresses?.slice().map((report_progress) => {
            if (
              report_progress.status === ReportProgressStatus.SUBMITTED ||
              report_progress.status === ReportProgressStatus.APPROVED
            ) {
              resultList.push(inProgressJob);
            }
          });
        });
    });
    setWaitingApprovalJobs([...resultList]);
  }, [data]);

  if (isLoading)
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <EmptyState type="noData" message="Loading..." />
      </Box>
    );

  if (waitingApprovalJobs && waitingApprovalJobs?.length == 0)
    return (
      <Box sx={{ py: 4 }}>
        <EmptyState
          type="noData"
          message={emptyText ?? "No Projects in Waiting Approval"}
        />
      </Box>
    );

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <Box sx={{ minWidth: { xs: 650, sm: "100%" }, background: `linear-gradient(${colors.green[100]}44, #fff)` }}>
        <List sx={{ py: 0 }}>
        {waitingApprovalJobs?.flatMap((job, jobIndex) => {
          const hiredTalents = job.resources?.filter(
            (resource) => !!resource.hired_at
          ) || [];
          return hiredTalents.map((hiredTalent, talentIndex) => {
            const sorted = hiredTalent.report_progresses
              ?.slice()
              .sort(
                (a: ReportProgress, b: ReportProgress) =>
                  new Date(b.check_in).getTime() -
                  new Date(a.check_in).getTime()
              );
            const submittedJob =
              sorted?.[0].status === ReportProgressStatus.SUBMITTED;
            const approvedJob =
              sorted?.[0].status === ReportProgressStatus.APPROVED;
            const locationText = !!job.customer
              ? `${job.customer.first_name} ${job.customer.last_name}`
              : !!job.location
                ? job.location.name
                : "?";

            const isLast = jobIndex === waitingApprovalJobs.length - 1 && talentIndex === hiredTalents.length - 1;

            return (
              <React.Fragment key={`${job.id}-${hiredTalent.resource_id}`}>
                <Box
                  onClick={() => navigate(`/works/${job.id}?t=1`)}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    backgroundColor: "transparent",
                    borderRadius: 2,
                    mb: { xs: 0.5, sm: 0.75 },
                    px: { xs: 1, sm: 2.5 },
                    py: { xs: 1, sm: 1.75 },
                    cursor: "pointer",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      backgroundColor: theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(0, 0, 0, 0.04)",
                      transform: "translateX(6px)",
                      boxShadow: theme.palette.mode === "dark"
                        ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                        : "0 4px 12px rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                  {/* WORK TITLE Column - flex: 5, align: left */}
                  <Box
                    sx={{
                      flex: { xs: 3, sm: 5 },
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                      flexDirection: "column",
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="body1"
                      className="text-primary-green"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", sm: "0.95rem" },
                        mb: { xs: 0.25, sm: 0.5 },
                        lineHeight: 1.4,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%",
                      }}
                    >
                      {`#${job.id} - ${job.title}`}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Place sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }} color="disabled" />
                      <Typography
                        variant="caption"
                        component="span"
                        sx={{
                          fontSize: { xs: "0.65rem", sm: "0.8rem" },
                          color: "text.secondary",
                          fontWeight: 400,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {locationText}
                      </Typography>
                    </Box>
                  </Box>

                  {/* MANAGER NAME Column - flex: 2, align: center */}
                  <Box
                    sx={{
                      flex: { xs: 1.5, sm: 2 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: "0.65rem", sm: "0.875rem" },
                        fontWeight: 500,
                        color: "text.primary",
                        textAlign: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {job.manager?.full_name || "N/A"}
                    </Typography>
                  </Box>

                  {/* INDEPENDENT PROFESSIONAL Column - flex: 2, align: center */}
                  <Box
                    sx={{
                      flex: { xs: 1.5, sm: 2 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: "0.65rem", sm: "0.875rem" },
                        fontWeight: 500,
                        color: "text.primary",
                        textAlign: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {hiredTalent.resource?.full_name || "N/A"}
                    </Typography>
                  </Box>

                  {/* STATUS Column - flex: 3, align: center */}
                  <Box
                    sx={{
                      flex: { xs: 2, sm: 3 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      minWidth: 0,
                    }}
                  >
                    {submittedJob && (
                      <Chip
                        label="WAITING FOR WORK APPROVAL"
                        size="small"
                        color="info"
                        sx={{
                          height: { xs: 20, sm: 24 },
                          fontSize: { xs: "0.6rem", sm: "0.75rem" },
                          fontWeight: 600,
                          mb: { xs: 0.25, sm: 0.75 },
                          boxShadow: theme.palette.mode === "dark"
                            ? "0 2px 4px rgba(0, 0, 0, 0.3)"
                            : "0 2px 4px rgba(0, 0, 0, 0.1)",
                        }}
                      />
                    )}
                    {approvedJob && (
                      <Chip
                        label="WAITING FOR WORK COMPLETION"
                        size="small"
                        color="success"
                        sx={{
                          height: { xs: 20, sm: 24 },
                          fontSize: { xs: "0.6rem", sm: "0.75rem" },
                          fontWeight: 600,
                          mb: { xs: 0.25, sm: 0.75 },
                          boxShadow: theme.palette.mode === "dark"
                            ? "0 2px 4px rgba(0, 0, 0, 0.3)"
                            : "0 2px 4px rgba(0, 0, 0, 0.1)",
                        }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: { xs: "0.6rem", sm: "0.75rem" },
                        color: "text.secondary",
                        fontWeight: 400,
                        textAlign: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatWithTimezone(
                        job.start_date,
                        job.timezone,
                        isMobile ? "MMM D, h:mmA" : "ddd, MMM D YYYY - h:mmA z"
                      )}
                    </Typography>
                  </Box>
                </Box>
                {!isLast && <Divider component="li" />}
              </React.Fragment>
            );
          });
        })}
      </List>
      </Box>
    </Box>
  );
};
export default ProjectNewsPanel;
